import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/layout/LoadingSpinner';
import { CandidateMessaging } from '../../components/admin/CandidateMessaging';
import { VideoPlayer } from '../../components/admin/VideoPlayer';
import { ProfileImage } from '../../components/ui/ProfileImage';
import { PDFPreviewModal } from '../../components/ui/PDFPreviewModal';
import { useViewport } from '../../hooks/useViewport';
import { unifiedApiService } from '../../services/unified';
import { useCandidateDetails, useApplicationVideo } from '../../hooks/unified/useAdminQueries';
import { useCandidateTestimonials } from '../../hooks/unified/useTestimonialsQueries';
import type { Application, CandidateProfile } from '../../services/unified/types';
import type { Testimonial } from '../../services/unified/testimonialsModule';

type TabType = 'overview' | 'applications' | 'messages' | 'resume' | 'testimonials';

export function AdminCandidateProfilePage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { data: candidate, isLoading, error } = useCandidateDetails(id || '');
  const { data: testimonials = [], isLoading: testimonialsLoading } = useCandidateTestimonials(id || '');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'blacklisted': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !candidate) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load candidate profile</h3>
            <p className="text-gray-600">{error instanceof Error ? error.message : 'Unknown error'}</p>
            <Link
              to="/admin/resumes"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Back to Resumes
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <CandidateHeader candidate={candidate} getStatusColor={getStatusColor} />

        {/* Candidate Summary */}
        <CandidateSummary candidate={candidate} />

        {/* Tabs */}
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Tab Content */}
        <div className="bg-white shadow rounded-lg">
          {activeTab === 'overview' && <OverviewTab candidate={candidate} />}
          {activeTab === 'applications' && <ApplicationsTab applications={candidate.applications || []} />}
          {activeTab === 'messages' && (
            <CandidateMessaging candidateId={candidate.user.id} candidateName={candidate.user.name || 'Unknown'} />
          )}
          {activeTab === 'resume' && <ResumeTab candidate={candidate} />}
          {activeTab === 'testimonials' && (
            <TestimonialsTab testimonials={testimonials} isLoading={testimonialsLoading} />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}


// Header Component
function CandidateHeader({ 
  candidate, 
  getStatusColor 
}: { 
  candidate: CandidateProfile; 
  getStatusColor: (status: string) => string;
}) {
  return (
    <div className="mb-6 sm:mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <ProfileImage
            src={candidate.user.avatar}
            alt={candidate.user.name || 'User'}
            name={candidate.user.name || 'Unknown'}
            size="lg"
          />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 truncate">{candidate.user.name || 'Unknown'}</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 truncate">{candidate.user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor('active')}`}>
            active
          </span>
          <Link
            to="/admin/resumes"
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded-md text-sm font-medium min-h-[44px] flex items-center"
          >
            Back
          </Link>
        </div>
      </div>
    </div>
  );
}

// Summary Component
function CandidateSummary({ candidate }: { candidate: CandidateProfile }) {
  return (
    <div className="bg-white shadow rounded-lg mb-6 sm:mb-8">
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
          <div>
            <div className="text-xs sm:text-sm font-medium text-gray-500">Phone</div>
            <div className="mt-1 text-sm text-gray-900 truncate">{candidate.profile?.phone || 'Not provided'}</div>
          </div>
          <div>
            <div className="text-xs sm:text-sm font-medium text-gray-500">Location</div>
            <div className="mt-1 text-sm text-gray-900 truncate">{candidate.profile?.location || 'Not provided'}</div>
          </div>
          <div>
            <div className="text-xs sm:text-sm font-medium text-gray-500">Resume</div>
            <div className="mt-1 text-sm font-semibold text-green-600">
              {candidate.resume_status || 'Not uploaded'}
            </div>
          </div>
          <div>
            <div className="text-xs sm:text-sm font-medium text-gray-500">Applications</div>
            <div className="mt-1 text-sm text-gray-900">{candidate.total_applications}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Tab Navigation
function TabNavigation({ 
  activeTab, 
  setActiveTab 
}: { 
  activeTab: TabType; 
  setActiveTab: (tab: TabType) => void;
}) {
  const { isMobile } = useViewport();
  const tabs: { id: TabType; name: string; shortName: string }[] = [
    { id: 'overview', name: 'Overview', shortName: 'Info' },
    { id: 'applications', name: 'Applications', shortName: 'Apps' },
    { id: 'messages', name: 'Messages', shortName: 'Msgs' },
    { id: 'resume', name: 'Resume Details', shortName: 'Resume' },
    { id: 'testimonials', name: 'Testimonials', shortName: 'Reviews' },
  ];

  return (
    <div className="mb-6 sm:mb-8 -mx-4 sm:mx-0 px-4 sm:px-0">
      <nav className="flex overflow-x-auto mobile-scroll gap-1 sm:gap-0 sm:space-x-8 pb-2 sm:pb-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 py-2.5 sm:py-2 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {isMobile ? tab.shortName : tab.name}
          </button>
        ))}
      </nav>
    </div>
  );
}


// Overview Tab
function OverviewTab({ candidate }: { candidate: CandidateProfile }) {
  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Candidate Overview</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Recent Activity</h4>
          <div className="space-y-3">
            {candidate.applications?.slice(0, 3).map((app, index) => (
              <div key={app.id || index} className="flex items-center text-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                <span className="text-gray-600">Applied to job</span>
                <span className="ml-auto text-gray-400">
                  {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : 'Unknown'}
                </span>
              </div>
            ))}
            {(!candidate.applications || candidate.applications.length === 0) && (
              <p className="text-sm text-gray-500">No recent activity</p>
            )}
          </div>
        </div>
        
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Top Skills</h4>
          {candidate.profile?.skills && candidate.profile.skills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {candidate.profile.skills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {skill}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No skills listed</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Applications Tab
function ApplicationsTab({ applications }: { applications: Application[] }) {
  const getApplicationStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': case 'hired': return 'bg-green-100 text-green-800';
      case 'interview': return 'bg-blue-100 text-blue-800';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Applications ({applications.length})</h3>
      <div className="space-y-4">
        {applications.map((application) => (
          <ApplicationCard 
            key={application.id} 
            application={application}
            getStatusColor={getApplicationStatusColor}
          />
        ))}
        {applications.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8">No applications yet</p>
        )}
      </div>
    </div>
  );
}

// Application Card Component
function ApplicationCard({ 
  application, 
  getStatusColor 
}: { 
  application: Application;
  getStatusColor: (status: string) => string;
}) {
  const [showVideo, setShowVideo] = useState(false);
  const { data: videoData, isLoading: videoLoading } = useApplicationVideo(application.id);

  const handleDownloadVideo = async () => {
    try {
      const blob = await unifiedApiService.applications.downloadApplicationVideo(application.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = videoData?.filename || 'video.mp4';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download video:', err);
      alert('Failed to download video. Please try again.');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <h4 className="text-md font-medium text-gray-900">Job Application</h4>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
              {application.status}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Applied {application.applied_at ? new Date(application.applied_at).toLocaleDateString() : 'Unknown'}
          </p>
        </div>
        {videoData && (
          <button
            onClick={() => setShowVideo(!showVideo)}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 min-h-[44px] px-3 py-2 bg-blue-50 rounded-md"
            title="View demo video"
          >
            <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {showVideo ? 'Hide' : 'Video'}
          </button>
        )}
      </div>
      
      {application.cover_letter && (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-700">{application.cover_letter}</p>
        </div>
      )}

      {showVideo && videoData && (
        <div className="mt-4">
          <VideoPlayer
            video={videoData}
            onDownload={handleDownloadVideo}
            onError={(err) => console.error('Video player error:', err)}
          />
        </div>
      )}

      {videoLoading && showVideo && (
        <div className="mt-4 flex items-center justify-center py-8">
          <LoadingSpinner size="sm" />
          <span className="ml-3 text-sm text-gray-600">Loading video...</span>
        </div>
      )}
    </div>
  );
}


// Resume Tab
function ResumeTab({ candidate }: { candidate: CandidateProfile }) {
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Use resume_id from backend response (preferred) or fallback to profile.lastResumeId
  const resumeId = candidate.resume_id || candidate.profile?.lastResumeId;
  const resumeFilename = candidate.resume_filename;
  const hasResume = resumeId && candidate.resume_status === 'uploaded';

  const handleDownload = async () => {
    if (!resumeId) return;
    
    setDownloading(true);
    try {
      const blob = await unifiedApiService.resume.downloadResume(resumeId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resumeFilename || `resume-${candidate.user.name || 'candidate'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download resume:', err);
      alert('Failed to download resume. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Resume Details</h3>
      <div className="space-y-6">
        {/* Experience */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Experience</h4>
          {candidate.experiences && candidate.experiences.length > 0 ? (
            candidate.experiences.map((exp) => (
              <div key={exp.id} className="border-l-4 border-primary-200 pl-4 mb-4">
                <h5 className="font-medium text-gray-900">{exp.title}</h5>
                <p className="text-sm text-gray-600">{exp.company}</p>
                <p className="text-xs text-gray-500">{exp.start_date} - {exp.end_date || 'Present'}</p>
                {exp.description && (
                  <p className="text-sm text-gray-700 mt-2">{exp.description}</p>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No experience listed</p>
          )}
        </div>
        
        {/* Education */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Education</h4>
          {candidate.education && candidate.education.length > 0 ? (
            candidate.education.map((edu) => (
              <div key={edu.id} className="border-l-4 border-blue-200 pl-4 mb-4">
                <h5 className="font-medium text-gray-900">{edu.degree}</h5>
                <p className="text-sm text-gray-600">{edu.institution}</p>
                {edu.field_of_study && (
                  <p className="text-sm text-gray-500">{edu.field_of_study}</p>
                )}
                <p className="text-xs text-gray-500">{edu.start_date} - {edu.end_date || 'Present'}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No education listed</p>
          )}
        </div>
        
        {/* Skills */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Skills</h4>
          {candidate.profile?.skills && candidate.profile.skills.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {candidate.profile.skills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {skill}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No skills listed</p>
          )}
        </div>
        
        {/* Resume File */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-3">Resume File</h4>
          {hasResume ? (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                    <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM9 13h6v2H9v-2zm0 4h6v2H9v-2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Resume PDF</p>
                    <p className="text-xs text-gray-500">Click to preview or download</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowPdfPreview(true)}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2.5 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors min-h-[44px]"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Preview
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 min-h-[44px]"
                  >
                    {downloading ? (
                      <>
                        <svg className="w-4 h-4 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 text-center">
              <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No resume uploaded</p>
            </div>
          )}
        </div>

        {/* PDF Preview Modal */}
        {showPdfPreview && resumeId && (
          <PDFPreviewModal
            isOpen={showPdfPreview}
            onClose={() => setShowPdfPreview(false)}
            resumeId={resumeId}
            title={`Resume - ${candidate.user.name || 'Candidate'}`}
            showDownload={true}
          />
        )}
      </div>
    </div>
  );
}

// Testimonials Tab
function TestimonialsTab({ 
  testimonials, 
  isLoading 
}: { 
  testimonials: Testimonial[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Testimonials ({testimonials.length})</h3>
      {testimonials.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No testimonials yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-gray-700 mb-3">{testimonial.content}</p>
                  {testimonial.rating && (
                    <div className="flex items-center space-x-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${i < testimonial.rating! ? 'text-yellow-400' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {testimonial.created_at ? new Date(testimonial.created_at).toLocaleDateString() : 'Unknown'}
                    </span>
                    {testimonial.approved ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Pending
                      </span>
                    )}
                    {testimonial.featured && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        ⭐ Featured
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
